// Plain-language copy for relationship refusals (AVA-602, showcase C: relationship-run-showcase.html).
//
// Every server code the relationship run, editor and workspace can surface maps to a title, a
// one-line reason and a next step. The raw code stays available as a small copyable detail and is
// never the headline. Codes without an entry return null so each caller keeps its own generic
// wording — an unmapped code must never render as an empty or invented message.
//
// Pure module: no Vue, no aliases, so node:test can import it directly.

export const REFUSAL_GROUPS = Object.freeze({
  rowFacility: 'Row’s Facility',
  otherFacility: 'Tag in another Facility',
});

const TONE = Object.freeze({ warning: 'warning', error: 'error' });

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function text(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

export function safeGuid(value) {
  const candidate = text(value);
  return GUID_PATTERN.test(candidate) ? candidate : null;
}

// ctx: { groupName, facilityName, recordName, tagTypeLabel, registerKind }
function groupName(ctx) { return text(ctx?.groupName) || 'the Facility Group'; }
function groupSubject(ctx) { return text(ctx?.groupName) || 'The Facility Group'; }
function facilityName(ctx) { return text(ctx?.facilityName) || 'this Facility'; }
function facilitySubject(ctx) { return text(ctx?.facilityName) || 'This row’s Facility'; }
function recordName(ctx) { return text(ctx?.recordName) || 'this Tag'; }

/** "Equipment Tags", or "These Tags" when the Tag type is unknown. */
export function tagsLabel(ctx) {
  const label = text(ctx?.tagTypeLabel).replace(/\s+tags?$/i, '');
  return label ? `${label} Tags` : 'These Tags';
}

function ownerNoun(ctx) {
  return ctx?.registerKind === 'facilityGroup' ? 'Facility Group' : 'Facility';
}

/* ---------------------------------------------------------------------------------------------
 * Row refusals: one run item that was left unchanged.
 * ------------------------------------------------------------------------------------------- */
const ROW_COPY = Object.freeze({
  'rowEligibilityRefused:RowAssignmentMissing': {
    group: REFUSAL_GROUPS.rowFacility,
    icon: 'mdi-map-marker-off-outline',
    title: () => 'Row has no Facility',
    text: () => 'Rows in a Separated register belong to one Facility. This row has none, so it can’t be linked to a Tag.',
    action: () => 'Set the row’s Facility in Data, then preview again.',
  },
  'rowEligibilityRefused:RowNotLiveMember': {
    group: REFUSAL_GROUPS.rowFacility,
    icon: 'mdi-map-marker-remove-outline',
    title: () => 'Facility left the Facility Group',
    text: ctx => `${facilitySubject(ctx)} is no longer in ${groupName(ctx)}, so this row can’t be linked.`,
    action: ctx => `Move the row to a Facility in ${groupName(ctx)}, or ask an admin to add ${facilityName(ctx)} back.`,
  },
  'rowEligibilityRefused:ParentUnavailable': {
    group: REFUSAL_GROUPS.rowFacility,
    icon: 'mdi-domain-off',
    title: () => 'Facility Group unavailable',
    text: ctx => `${groupSubject(ctx)} was archived or couldn’t be read, so no row can be linked.`,
    action: () => 'Ask an admin to restore the Facility Group, then preview again.',
  },
  'rowEligibilityRefused:AssignmentModeUnresolved': {
    group: REFUSAL_GROUPS.rowFacility,
    icon: 'mdi-help-rhombus-outline',
    title: () => 'Register sharing not set',
    text: () => 'This register doesn’t say whether its rows are Shared or Separated across the Facility Group.',
    action: () => 'Ask an admin to set Shared or Separated on the register.',
  },
  // Any other refusal the eligibility check adds later still gets a readable row.
  rowEligibilityRefused: {
    group: REFUSAL_GROUPS.rowFacility,
    icon: 'mdi-map-marker-alert-outline',
    title: () => 'Row can’t be used in this Facility Group',
    text: ctx => `This row’s Facility couldn’t be confirmed as part of ${groupName(ctx)}, so it can’t be linked.`,
    action: () => 'Check the row’s Facility in Data, then preview again.',
  },
  poiContextMissing: {
    group: REFUSAL_GROUPS.rowFacility,
    icon: 'mdi-map-marker-question-outline',
    title: () => 'Facility not known for this row',
    text: () => 'The row doesn’t name a Facility, so AIVA can’t tell which Facility’s position to use.',
    action: () => 'Set the row’s Facility in Data, then preview again.',
  },
  // AVA-602 GAP-16 (N33): a Shared row names a Tag that only a member Facility has.
  sameNameInMemberFacility: {
    group: REFUSAL_GROUPS.otherFacility,
    icon: 'mdi-content-duplicate',
    title: () => 'Tag already exists in a Facility',
    text: ctx => `${text(ctx?.tagName) || 'A Tag with this name'} already exists in ${text(ctx?.facilityName) || `a Facility of ${groupName(ctx)}`}. This register uses only ${groupName(ctx)} Tags, so AIVA didn’t create a second Tag with the same name.`,
    action: () => 'Rename one of them, or move the Tag to the Facility Group, then preview again.',
  },
  candidateObservationUnavailable: {
    group: REFUSAL_GROUPS.otherFacility,
    icon: 'mdi-map-marker-alert-outline',
    title: () => 'Tag may belong to another Facility',
    text: ctx => `AIVA couldn’t confirm a Tag named ${recordName(ctx)} in ${facilityName(ctx)}, so it didn’t create one. It may already exist in another Facility of ${groupName(ctx)}.`,
    action: ctx => `Check which Facility ${recordName(ctx)} is in. Move the row there, or rename one of them.`,
  },
  candidateObservationIncomplete: {
    group: REFUSAL_GROUPS.otherFacility,
    icon: 'mdi-magnify-close',
    title: () => 'Tag search didn’t finish',
    text: ctx => `AIVA couldn’t finish checking every Facility in ${groupName(ctx)} for ${recordName(ctx)}. Nothing will change.`,
    action: () => 'Preview again. If it happens again, contact support with the code below.',
  },
});

/** Copy key for an outcome item, or null when the row has no refusal copy. */
export function rowRefusalKey(item) {
  if (item?.itemKind !== 'outcome') return null;
  const code = text(item.reason?.code) || text(item.outcomeCode);
  if (code === 'rowEligibilityRefused') {
    const refusal = text(item.reason?.data?.refusal);
    return ROW_COPY[`${code}:${refusal}`] ? `${code}:${refusal}` : code;
  }
  return ROW_COPY[code] ? code : null;
}

function rowCodeLabel(key) { return key.replace(':', ' · '); }

/** { key, group, icon, tone, title, text, action, code } for a refused row, or null. */
export function rowRefusalCopy(item, ctx = {}) {
  const key = rowRefusalKey(item);
  if (!key) return null;
  const entry = ROW_COPY[key];
  return {
    key,
    group: entry.group,
    icon: entry.icon,
    tone: TONE.warning,
    title: entry.title(ctx),
    text: entry.text(ctx),
    action: entry.action(ctx),
    code: rowCodeLabel(key === 'rowEligibilityRefused' && text(item.reason?.data?.refusal)
      ? `${key}:${text(item.reason.data.refusal)}`
      : key),
  };
}

/**
 * Refused rows grouped for the attention strip, in first-seen order.
 * Returns { total, groups: [{ name, entries: [{ key, title, icon, count }] }] }.
 */
export function groupRowRefusals(items, ctx = {}) {
  const groups = new Map();
  let total = 0;
  (Array.isArray(items) ? items : []).forEach((item) => {
    const copy = rowRefusalCopy(item, ctx);
    if (!copy) return;
    total += 1;
    if (!groups.has(copy.group)) groups.set(copy.group, new Map());
    const entries = groups.get(copy.group);
    const entry = entries.get(copy.key) || { key: copy.key, title: copy.title, icon: copy.icon, count: 0 };
    entries.set(copy.key, { ...entry, count: entry.count + 1 });
  });
  return {
    total,
    groups: Array.from(groups.entries()).map(([name, entries]) => ({ name, entries: Array.from(entries.values()) })),
  };
}

/* ---------------------------------------------------------------------------------------------
 * Whole-run refusals: preview, approval, editor save.
 * ------------------------------------------------------------------------------------------- */
const PROBLEM_COPY = Object.freeze({
  GROUP_CREATE_UNSUPPORTED_FOR_TAG_TYPE: {
    icon: 'mdi-plus-circle-off-outline',
    tone: TONE.error,
    title: ctx => `${tagsLabel(ctx)} can’t be created here`,
    text: ctx => `A Shared Facility Group register can link existing ${tagsLabel(ctx)}, but it can’t create new ones.`,
    action: () => 'In the relationship, set “When no target matches” to “Leave unresolved”, or use a Separated register.',
  },
  GROUP_WRITE_FORBIDDEN: {
    icon: 'mdi-account-lock-outline',
    tone: TONE.error,
    title: () => 'You can’t change Tags in every Facility',
    text: ctx => `A Facility Group register changes Tags in all of its Facilities. Your access doesn’t cover all of ${groupName(ctx)}.`,
    action: () => 'Ask an admin for Tag update permission in every Facility of the Facility Group.',
  },
  GROUP_FENCE_BUSY: {
    icon: 'mdi-timer-sand',
    tone: TONE.warning,
    title: () => 'Facility Group is busy',
    text: ctx => `Another run or change is using ${groupName(ctx)} right now.`,
    action: () => 'Wait a moment, then approve again. The preview stays valid until it expires.',
  },
  'PREVIEW_STALE:group': {
    icon: 'mdi-refresh-circle',
    tone: TONE.warning,
    title: ctx => `${groupSubject(ctx)} changed after this preview`,
    text: () => 'A Facility joined or left the Facility Group, or a row’s Facility changed. The saved decisions may no longer be right.',
    action: () => 'Build a new preview.',
  },
  TARGET_SCOPE_UNSUPPORTED: {
    icon: 'mdi-tag-off-outline',
    tone: TONE.error,
    title: ctx => `${tagsLabel(ctx)} can’t be used here`,
    text: ctx => `This Tag type can’t be used with this ${ownerNoun(ctx)} register, or can’t be edited by this run.`,
    action: () => 'Pick another Tag type in the relationship, or ask an admin to check Tag permissions.',
  },
  REGISTER_SCOPE_CHANGED: {
    icon: 'mdi-swap-horizontal-bold',
    tone: TONE.warning,
    title: () => 'Register moved',
    text: () => 'This register now belongs to a different Facility or Facility Group than when the relationship was set up.',
    action: () => 'Create a new relationship for the register.',
  },
  'PREVIEW_STALE:plan': {
    icon: 'mdi-file-refresh-outline',
    tone: TONE.warning,
    title: () => 'Preview changed after you reviewed it',
    text: () => 'The saved preview no longer matches the decisions shown here, so it can’t be approved.',
    action: () => 'Build a new preview and review it again.',
  },
  'PREVIEW_STALE:access': {
    icon: 'mdi-account-key-outline',
    tone: TONE.warning,
    title: () => 'Your access changed after this preview',
    text: () => 'Your permissions changed since this preview was built, so it can’t be approved as it stands.',
    action: () => 'Build a new preview. If approval stays locked, ask an admin to check your access.',
  },
  'PREVIEW_STALE:owner': {
    icon: 'mdi-swap-horizontal-bold',
    tone: TONE.warning,
    title: () => 'Register ownership changed after this preview',
    text: () => 'This register’s Facility or Facility Group changed, or couldn’t be confirmed, after this preview was built.',
    action: () => 'Build a new preview.',
  },
  'PREVIEW_STALE:relationship': {
    icon: 'mdi-vector-link',
    tone: TONE.warning,
    title: () => 'Relationship changed after this preview',
    text: () => 'The relationship was edited, disabled or removed after this preview was built.',
    action: () => 'Check the relationship, then build a new preview.',
  },
  'PREVIEW_STALE:fields': {
    icon: 'mdi-form-textbox',
    tone: TONE.warning,
    title: () => 'Tag fields changed after this preview',
    text: () => 'A mapped Tag field was changed, disabled or removed after this preview was built.',
    action: () => 'Build a new preview. If the field was removed on purpose, update the relationship’s field mapping first.',
  },
  'PREVIEW_STALE:targets': {
    icon: 'mdi-tag-arrow-right-outline',
    tone: TONE.warning,
    title: () => 'Tags changed after this preview',
    text: () => 'A Tag, or a row’s link to a Tag, changed after this preview was built.',
    action: () => 'Build a new preview to see the current decisions.',
  },
  FACILITY_VIEW_REQUIRED: {
    icon: 'mdi-eye-off-outline',
    tone: TONE.warning,
    title: () => 'Some Facilities are hidden from you',
    text: () => 'You can’t see every Facility in this preview, so approval is locked.',
    action: () => 'Ask an admin for access, or ask someone with access to approve it.',
  },
  REGISTER_BUSY: {
    icon: 'mdi-timer-sand',
    tone: TONE.warning,
    title: () => 'Register is busy',
    text: () => 'Another run or change is in progress on this register.',
    action: () => 'Try again in a minute.',
  },
});

// PREVIEW_STALE `reason` values (approval refusals) grouped by what the user has to understand.
// Engine codes from the confirmation service, scope policy and revalidator; any other reason keeps
// today's generic stale message.
const STALE_REASON_FAMILIES = Object.freeze([
  // The Facility Group, or a row's Facility, moved under the preview.
  { key: 'PREVIEW_STALE:group', test: reason => /^(group|poi)/.test(reason) },
  {
    key: 'PREVIEW_STALE:plan',
    test: reason => ['planHashMismatch', 'CURRENT_PLAN_ITEM_MISMATCH', 'persistedItemSetIncomplete',
      'persistedItemKindInvalid', 'persistedScopeBindingMalformed'].includes(reason),
  },
  { key: 'PREVIEW_STALE:access', test: reason => ['authorityContextMismatch', 'mapAuthorityAcquired'].includes(reason) },
  {
    key: 'PREVIEW_STALE:owner',
    test: reason => ['registerOwnerUnavailable', 'registerOwnerUnsupported', 'fixedFacilityScopeMismatch',
      'scopeBindingMissing', 'scopeBindingRefused', 'containingBindingInvalid'].includes(reason),
  },
  {
    key: 'PREVIEW_STALE:relationship',
    test: reason => ['relationshipDisabled', 'relationshipMissing', 'relationshipQuarantined', 'configVersionChanged'].includes(reason),
  },
  {
    key: 'PREVIEW_STALE:fields',
    test: reason => /^(destination|dynamicDefinition|targetSchema)/.test(reason),
  },
  {
    key: 'PREVIEW_STALE:targets',
    test: reason => /^(expected|targetConcurrency|targetScope)/.test(reason)
      || ['targetMissing', 'targetBindingChanged', 'ownedLinkAppeared', 'chosenTargetKindMismatch'].includes(reason),
  },
]);

/** The approval refusal reason, whether the API sends a string or a `{ code }` object. */
export function problemReasonCode(problem) {
  const reason = problem?.reason;
  if (typeof reason === 'string') return text(reason);
  return text(reason?.code);
}

function problemKey(problem) {
  const code = text(problem?.code);
  if (code === 'PREVIEW_STALE') {
    const reason = problemReasonCode(problem);
    const family = reason ? STALE_REASON_FAMILIES.find(entry => entry.test(reason)) : null;
    return family ? family.key : null;
  }
  return PROBLEM_COPY[code] ? code : null;
}

/** { key, icon, tone, title, text, action, code } for a whole-run refusal, or null. */
export function problemCopy(problem, ctx = {}) {
  const key = problemKey(problem);
  if (!key) return null;
  const entry = PROBLEM_COPY[key];
  const reason = problemReasonCode(problem);
  return {
    key,
    icon: entry.icon,
    tone: entry.tone,
    title: entry.title(ctx),
    text: entry.text(ctx),
    action: entry.action(ctx),
    code: reason ? `${problem.code} · ${reason}` : problem.code,
  };
}

/* ---------------------------------------------------------------------------------------------
 * Editor field errors. The API reports both scope refusals on `/targetScopeKind`, which is not a
 * form field; route them by code to the control the user has to change.
 * ------------------------------------------------------------------------------------------- */
const EDITOR_FIELD_BY_CODE = Object.freeze({
  TARGET_SCOPE_UNSUPPORTED: {
    field: 'targetKind',
    message: () => 'Not available for this register. Pick another Tag type.',
  },
  GROUP_CREATE_UNSUPPORTED_FOR_TAG_TYPE: {
    field: 'onMiss',
    message: ctx => `Shared Facility Group registers can’t create ${tagsLabel(ctx)}. Choose “Leave unresolved”.`,
  },
});

/** Friendly messages for one editor field from `/targetScopeKind` errors. */
export function editorScopeFieldErrors(problem, field, ctx = {}) {
  const errors = Array.isArray(problem?.errors) ? problem.errors : [];
  return errors
    .filter(entry => String(entry?.path || '').toLowerCase() === '/targetscopekind')
    .map(entry => EDITOR_FIELD_BY_CODE[entry.code || problem.code])
    .filter(mapping => mapping && mapping.field === field)
    .map(mapping => mapping.message(ctx));
}

/** True when an error is routed by code, so the generic path matcher must skip it. */
export function isRoutedScopeError(entry) {
  return String(entry?.path || '').toLowerCase() === '/targetscopekind';
}

/* ---------------------------------------------------------------------------------------------
 * Workspace toasts.
 * ------------------------------------------------------------------------------------------- */
const OPERATION_TITLES = Object.freeze({
  enable: 'Relationship could not be enabled',
  disable: 'Relationship could not be disabled',
  delete: 'Relationship could not be deleted',
  validate: 'Relationship validation failed',
  refresh: 'Status could not be refreshed',
});

const TOAST_OVERRIDES = Object.freeze({
  GROUP_WRITE_FORBIDDEN: {
    enable: ctx => ({
      title: 'You can’t enable this relationship',
      text: `Enabling it changes Tags in every Facility of ${groupName(ctx)}, and your access doesn’t cover all of them.`,
    }),
    delete: ctx => ({
      title: 'You can’t delete this relationship',
      text: `Deleting it changes Tags in every Facility of ${groupName(ctx)}, and your access doesn’t cover all of them.`,
    }),
  },
});

/**
 * { title, text, action, code } for a failed workspace command. Unmapped codes keep the server's
 * sentence under a readable title, so nothing the server said is lost.
 */
export function toastCopy(problem, operation, ctx = {}, fallbackText = '') {
  const code = text(problem?.code) || null;
  const override = code && TOAST_OVERRIDES[code]?.[operation];
  if (override) {
    return { ...override(ctx), action: PROBLEM_COPY[code].action(ctx), code };
  }
  const mapped = problemCopy(problem, ctx);
  if (mapped) return { title: mapped.title, text: mapped.text, action: mapped.action, code: mapped.code };
  return {
    title: OPERATION_TITLES[operation] || 'Relationship request failed',
    text: text(problem?.detail) || fallbackText,
    action: '',
    code,
  };
}

export function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[character]);
}

/** HTML for vuetify-dialog notify, which renders `text` through innerHTML: every part is escaped. */
export function toastHtml(copy) {
  const parts = [`<strong>${escapeHtml(copy.title)}</strong>`];
  if (copy.text) parts.push(`<div>${escapeHtml(copy.text)}</div>`);
  if (copy.action) parts.push(`<div>${escapeHtml(copy.action)}</div>`);
  if (copy.code) parts.push(`<div style="opacity:.75;font-size:12px">Code ${escapeHtml(copy.code)}</div>`);
  return parts.join('');
}

/* ---------------------------------------------------------------------------------------------
 * Run result per row, from the item's end status. No extra API needed.
 * terminalClass: succeeded | completedWithFieldErrors | failed | drifted | null
 * ------------------------------------------------------------------------------------------- */
const SUCCESS_LABELS = Object.freeze({
  repoint: 'Link moved',
  create: 'Tag created',
  cleanup: 'Link removed',
});

export function runItemResult(item) {
  if (item?.itemKind !== 'action') return { label: 'No change', tone: 'none', icon: null };
  switch (item.terminalClass) {
    case 'succeeded':
      return { label: SUCCESS_LABELS[item.action] || 'Applied', tone: 'success', icon: 'mdi-check-circle-outline' };
    case 'completedWithFieldErrors':
      return { label: 'Some fields not written', tone: 'warning', icon: 'mdi-alert-circle-outline' };
    case 'drifted':
      return { label: 'Changed since preview', tone: 'warning', icon: 'mdi-clock-alert-outline' };
    case 'failed':
      return { label: 'Not changed', tone: 'error', icon: 'mdi-close-circle-outline' };
    default:
      return { label: 'Not run', tone: 'none', icon: null };
  }
}

/* ---------------------------------------------------------------------------------------------
 * Changed link (repoint): old and new Tag names.
 * ------------------------------------------------------------------------------------------- */
function navigationFacility(target) {
  const explicit = safeGuid(target?.navigationFacilityId);
  if (explicit) return explicit;
  return target?.targetScopeKind === 'facility' ? safeGuid(target.targetScopeId) : null;
}

function chosenTargetName(item) {
  return text(item?.chosenTargetDisplayName)
    || text(item?.chosenTarget?.displayName)
    || text(item?.actionPayload?.chosenTarget?.displayName)
    || text(item?.targetDisplayName);
}

/**
 * Expanded-row copy for a changed link: { label, text, action } before approval, or what happened
 * once the row ran. `sourceValue` is the row's own value; empty means it is hidden or unavailable.
 */
export function repointExplanation(targets, { sourceValue = '', terminalClass = null } = {}) {
  if (!targets) return null;
  const unnamed = targets.previous.unnamed === true;
  const from = unnamed ? 'the previous Tag' : targets.previous.name;
  const fromSubject = unnamed ? 'The previous Tag' : targets.previous.name;
  const to = targets.next.name;
  if (terminalClass === 'succeeded') {
    return { label: 'What happened', text: `The link moved from ${from} to ${to}. ${fromSubject} is no longer linked to this row.`, action: '' };
  }
  if (terminalClass === 'drifted') {
    return { label: 'What happened', text: `${to} changed after approval, so the link was left on ${from}.`, action: '' };
  }
  if (terminalClass === 'failed' || terminalClass === 'completedWithFieldErrors') {
    return { label: 'What happened', text: `The link was left on ${from}.`, action: 'Build a new preview to see the current decision.' };
  }
  const matched = text(sourceValue) ? ` Matched from the source value “${text(sourceValue)}”.` : '';
  return {
    label: 'What happens',
    text: `${targets.previous.name} → ${to}.${matched}`,
    action: `Approve to move this row’s link from ${from} to ${to}. If the old link was right, fix the source value instead.`,
  };
}

export const PREVIOUS_TAG_FALLBACK = 'Previous Tag';

/**
 * { previous, next } Tag references for a repoint row, or null when the API has not named the new
 * Tag (the caller then keeps today's unnamed wording). The old Tag's name is omitted when it was
 * hard-deleted; it then reads "Previous Tag" and is not navigable.
 * Each reference: { name, targetId, facilityId, unnamed? } — facilityId null means not navigable.
 */
export function repointTargets(item) {
  if (item?.itemKind !== 'action' || item.action !== 'repoint') return null;
  const previous = item.actionPayload?.previousTarget || item.previousTarget || null;
  const previousName = text(previous?.displayName);
  const nextName = chosenTargetName(item);
  if (!nextName) return null;
  return {
    previous: previousName
      ? { name: previousName, targetId: safeGuid(previous.targetId), facilityId: navigationFacility(previous) }
      : { name: PREVIOUS_TAG_FALLBACK, targetId: null, facilityId: null, unnamed: true },
    next: {
      name: nextName,
      targetId: safeGuid(item.chosenTargetId),
      facilityId: navigationFacility({
        navigationFacilityId: item.chosenTarget?.navigationFacilityId || item.navigationFacilityId,
        targetScopeKind: item.targetScopeKind,
        targetScopeId: item.targetScopeId,
      }),
    },
  };
}
